/**
 * Payment-plan utilities — Phase B Feature 6.
 *
 * Resolution rules (highest priority first):
 *   1. `unit.payment_plan_override` (full ad-hoc plan, never matched by id)
 *   2. `project.payment_plans[id === unit.payment_plan_id]`
 *   3. project default (the one with `default: true`, or first)
 *   4. nothing — calculator hidden
 */

export function listPaymentPlans(project) {
  return (project && project.payment_plans) || [];
}

export function resolvePlansForUnit(project, unit) {
  const projectPlans = listPaymentPlans(project);
  const plans = projectPlans.map((p) => ({ ...p, _source: "project" }));
  if (unit && unit.payment_plan_override) {
    plans.unshift({ ...unit.payment_plan_override, id: "unit_override", _source: "unit" });
  }
  return plans;
}

export function defaultPlanFor(project, unit) {
  const plans = resolvePlansForUnit(project, unit);
  if (!plans.length) return null;
  if (unit?.payment_plan_override) return plans.find((p) => p.id === "unit_override");
  if (unit && unit.payment_plan_id) {
    const m = plans.find((p) => p.id === unit.payment_plan_id);
    if (m) return m;
  }
  return plans.find((p) => p.default || p.is_default) || plans[0];
}

export function currencyDigits(currency) {
  try {
    return new Intl.NumberFormat("en", { style: "currency", currency: currency || "USD" }).resolvedOptions().maximumFractionDigits;
  } catch { return 2; }
}

/**
 * Pure calculator — returns the rendered breakdown for a price+plan
 * combination. All numbers are pre-rounded. Returns `null` if the plan
 * is meaningless (no price, no plan, etc.) so the UI can hide cleanly.
 */
export function computeBreakdown(plan, price, currency) {
  const p = Number(price);
  if (!plan || !Number.isFinite(p) || p <= 0) return null;
  const dpPct = Number(plan.down_payment_pct ?? 0);
  const months = Number(plan.months ?? 0);
  const discountPct = Number(plan.discount_pct ?? 0);
  const discountAmt = Number(plan.discount_amount ?? 0);
  const vatPct = Number(plan.vat_pct ?? 0);
  if (![dpPct, months, discountPct, discountAmt, vatPct].every(Number.isFinite)
      || [dpPct, discountPct, vatPct].some((n) => n < 0 || n > 100)
      || discountAmt < 0 || months < 0 || !Number.isInteger(months)) return null;
  const resolvedCurrency = plan.currency || currency || "";
  const scale = 10 ** currencyDigits(resolvedCurrency);
  const rounded = (n) => Math.round((n + Number.EPSILON) * scale) / scale;

  // Discount → effective price → VAT → final total.
  let discount = 0;
  if (discountPct) discount = (p * discountPct) / 100;
  if (discountAmt) discount += discountAmt;
  if (discount > p) return null;
  discount = rounded(discount);
  const subtotal = rounded(p - discount);
  const vat = rounded(vatPct ? (subtotal * vatPct) / 100 : 0);
  const total = rounded(subtotal + vat);
  const downPayment = rounded((total * dpPct) / 100);
  const remaining = rounded(total - downPayment);
  // All ordinary instalments are rounded DOWN to a currency minor unit;
  // the final instalment includes the remainder. Displayed sums now agree.
  const monthly = months > 0 ? Math.floor(Math.round(remaining * scale) / months) / scale : 0;
  const finalMonthly = months > 0 ? rounded(remaining - monthly * (months - 1)) : 0;

  return {
    currency: resolvedCurrency,
    list_price: rounded(p),
    discount,
    subtotal,
    vat_pct: vatPct,
    vat,
    total,
    down_payment_pct: dpPct,
    down_payment: downPayment,
    remaining,
    months,
    monthly,
    final_monthly: finalMonthly,
    has_final_adjustment: finalMonthly !== monthly,
    is_cash: months === 0 && dpPct >= 100,
  };
}

export function formatMoney(amount, currency, lang = "en") {
  if (amount == null || !Number.isFinite(amount)) return "—";
  const digits = currencyDigits(currency);
  return `${currency || ""} ${amount.toLocaleString(lang, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`.trim();
}
